import Fuse from 'fuse.js';
import { PaginatedApiArg } from 'lib-shared-api-utils';
import {
  getAsyncStatus,
  getAsyncStatusFromRtkQueryStates,
} from 'lib-shared-auth';
import {
  AsyncStatus,
  CategorizedApiResponse,
  PaginatedApiResponse,
} from 'lib-shared-types-api';
import { useCallback, useMemo } from 'react';

import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import {
  CategorizedListCategory,
  UseCategorizedListOptions,
  UseCategorizedListResult,
} from './useCategorizedList.types';

/**
 * A hook that fetches non-paginated categories of items as well as paginated
 * items of the same type. These are then concatenated in a continuous
 * categorized sequence that appears paginated.
 */
export const useCategorizedList = <
  TCategoryApiResponse extends CategorizedApiResponse<TItem>,
  TPaginatedApiArg extends PaginatedApiArg,
  TPaginatedApiResponse extends PaginatedApiResponse,
  TItem extends Record<string, unknown>,
>({
  apis: {
    categorized: {
      result: categorizedResult,
      searchOptions: fuseOptions,
      changeArgItemPredicate,
    },
    paginated: paginatedApi,
  },
  identityFn,
}: UseCategorizedListOptions<
  TCategoryApiResponse,
  TPaginatedApiArg,
  TPaginatedApiResponse,
  TItem
>): UseCategorizedListResult<TPaginatedApiArg, TCategoryApiResponse, TItem> => {
  const {
    items: paginatedItems,
    asyncStatus: paginatedItemsAsyncStatus,
    arg: paginatedItemsArg,
    search: paginatedItemsSearch,
    changeArg: paginatedItemsChangeArg,
    refetchItems: paginatedItemsRefetchItems,
    hasNextPage,
    isPaginating,
    nextPage,
  } = usePaginatedEndpoint({
    ...paginatedApi,
    infinite: true,
  });

  const categories = useMemo<CategorizedListCategory<TItem>[]>(() => {
    const categories = categorizedResult.data?.data.categories ?? [];

    if (
      !isPaginating &&
      (paginatedItemsAsyncStatus === 'pending' || categorizedResult.isFetching)
    ) {
      return new Array<CategorizedListCategory<TItem>>({
        id: 'paginated',
        items: [],
      });
    }

    return new Array<CategorizedListCategory<TItem>>(
      ...categories
        .map(({ id, items }) => {
          const fuse = new Fuse<TItem>(items, fuseOptions);

          if (paginatedItemsArg?.searchTerm) {
            items = fuse
              .search(paginatedItemsArg.searchTerm)
              .map(({ item }) => item);
          }

          if (paginatedItemsArg && changeArgItemPredicate) {
            items = items.filter((item) =>
              changeArgItemPredicate(item, paginatedItemsArg),
            );
          }

          return { id, items };
        })
        .filter((category) => category.items.length),
      { id: 'paginated', items: paginatedItems },
    );
    // Recompute only when the true dependencies changes as it would otherwise
    // execute too often due when the hook options are passed in anonymously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categorizedResult.data?.data.categories,
    categorizedResult.isFetching,
    isPaginating,
    paginatedItems,
    paginatedItemsArg,
    paginatedItemsAsyncStatus,
  ]);

  const asyncStatus = useMemo<AsyncStatus>(
    () =>
      getAsyncStatus([
        paginatedItemsAsyncStatus,
        getAsyncStatusFromRtkQueryStates(categorizedResult),
      ]),
    [categorizedResult, paginatedItemsAsyncStatus],
  );

  const favorites = useMemo<ReadonlySet<ReturnType<typeof identityFn>>>(
    () =>
      new Set(
        (categorizedResult.data?.data.categories ?? [])
          .filter((category) => category.id === 'favorite')
          .flatMap((category) => category.items)
          .map(identityFn),
      ),
    [categorizedResult.data?.data.categories, identityFn],
  );

  const refetch = useCallback(() => {
    categorizedResult.refetch();
    paginatedItemsRefetchItems();
  }, [categorizedResult, paginatedItemsRefetchItems]);

  return {
    categories,
    asyncStatus,
    hasNextPage,
    favorites,
    arg: paginatedItemsArg,
    changeArg: paginatedItemsChangeArg,
    search: paginatedItemsSearch,
    refetch,
    nextPage,
  };
};
